import React, { useState, useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { collection, query, where, addDoc, serverTimestamp, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Plus, Receipt, CheckCircle, AlertCircle, Home, Users } from 'lucide-react';

const SharedAccountDetail = () => {
    const { id } = useParams();
    const { currentUser } = useAuth();

    const [account, setAccount] = useState(null);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);

    // Stato form aggiunta
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [paidBy, setPaidBy] = useState(currentUser?.uid || '');
    const [excludeMe, setExcludeMe] = useState(false); // Es. raccolta affitto: io ricevo ma non partecipo alla spesa

    useEffect(() => {
        if (!currentUser) return;

        const fetchAccount = async () => {
            try {
                const docRef = doc(db, 'sharedAccounts', id);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    if (!data.members.includes(currentUser.uid)) {
                        setAccount('unauthorized');
                    } else {
                        setAccount({ id: docSnap.id, ...data });
                    }
                } else {
                    setAccount('not-found');
                }
            } catch (error) {
                console.error("Errore recupero conto:", error);
            }
        };

        fetchAccount();

        const expensesQuery = query(
            collection(db, 'expenses'),
            where('accountId', '==', id)
        );

        const unsubscribe = onSnapshot(expensesQuery, (snapshot) => {
            const expensesList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            expensesList.sort((a, b) => {
                const timeA = a.timestamp?.toMillis() || 0;
                const timeB = b.timestamp?.toMillis() || 0;
                return timeB - timeA;
            });

            setExpenses(expensesList);
            setLoading(false);
        });

        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, currentUser]);

    // Quando l'account viene caricato, assicura che il dropdown "Pagato da" sia impostato sul currentUser corrente
    useEffect(() => {
        if (currentUser && !paidBy) {
            setPaidBy(currentUser.uid);
        }
    }, [currentUser, paidBy]);


    const handleAddExpense = async (e) => {
        e.preventDefault();
        if (!amount || isNaN(amount) || amount <= 0 || !description.trim() || !paidBy) return;

        // Determina chi partecipa alla spesa
        // Se `excludeMe` è true, rimuoviamo l'UID del currentUser dall'array di chi deve pagare.
        // Altrimenti, partecipano tutti i membri (inclusi i pending, che usano la chiave "pending_email")

        // Trova tutte le chiavi utente in memberDetails
        const allParticipants = Object.keys(account.memberDetails);
        const splitAmong = excludeMe ? allParticipants.filter(uid => uid !== currentUser.uid) : allParticipants;

        if (splitAmong.length === 0) {
            alert("Errore: La spesa deve essere divisa tra almeno una persona.");
            return;
        }

        try {
            await addDoc(collection(db, 'expenses'), {
                accountId: id,
                paidBy: paidBy,
                amount: parseFloat(amount),
                description: description.trim(),
                splitAmong: splitAmong, // Nuova array che ci dice chi "deve" la quota
                timestamp: serverTimestamp()
            });

            setAmount('');
            setDescription('');
            setExcludeMe(false);
            setPaidBy(currentUser.uid); // resetta
            setIsAddOpen(false);
        } catch (error) {
            console.error("Errore aggiunta spesa:", error);
            alert("Errore durante il salvataggio della spesa.");
        }
    };

    if (!currentUser) return <Navigate to="/" />;
    if (account === 'unauthorized' || account === 'not-found') return <Navigate to="/shared-expenses" />;

    if (loading || !account) {
        return <div style={{ textAlign: 'center', padding: '40px' }}>Caricamento conto in corso...</div>;
    }

    // --- LOGICA DI CALCOLO SALDI (SPLITWISE-STYLE) --- //

    // Inizializza i totali per ogni utente presente nel conto
    const balances = {};
    const totalPaidByUser = {};

    Object.keys(account.memberDetails).forEach(uid => {
        balances[uid] = 0; // Se +, deve ricevere. Se -, deve dare.
        totalPaidByUser[uid] = 0;
    });

    expenses.forEach(exp => {
        const { amount, paidBy, splitAmong } = exp;

        // Aggiungi l'importo pagato a totalPaidByUser e al saldo di chi ha pagato
        if (totalPaidByUser[paidBy] !== undefined) {
            totalPaidByUser[paidBy] += amount;
            balances[paidBy] += amount;
        }

        // Dividi la spesa
        // Reto-compatibilità: le vecchie spese non hanno `splitAmong`, quindi si divide tra tutti i membri "conosciuti"
        const participants = splitAmong || Object.keys(account.memberDetails);

        if (participants.length > 0) {
            const share = amount / participants.length;
            participants.forEach(uid => {
                if (balances[uid] !== undefined) {
                    balances[uid] -= share; // Sottrae la quota (aumenta il debito)
                }
            });
        }
    });

    const myBalance = balances[currentUser.uid] || 0;

    // Colori per il semaforo dei saldi
    const getBalanceColor = (bal) => {
        if (bal > 0.01) return "#006a4e"; // Deve ricevere (Verde)
        if (bal < -0.01) return "#d32f2f"; // Deve dare (Rosso)
        return "#555"; // In pari (Grigio)
    };

    return (
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '80px 20px 40px', minHeight: '80vh' }}>
            <div style={{ marginBottom: '20px' }}>
                <Link to="/shared-expenses" style={{ color: 'var(--text-secondary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <ArrowLeft size={16} /> Torna ai Conti
                </Link>
            </div>

            {/* HEADER DASHBOARD */}
            <div style={{
                backgroundColor: 'var(--bg-primary)', borderRadius: '16px', overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: '30px'
            }}>
                <div style={{ backgroundColor: '#006a4e', padding: '30px 20px', color: 'white', textAlign: 'center' }}>
                    <div style={{ display: 'inline-flex', padding: '10px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '50%', marginBottom: '16px' }}>
                        {account.name ? <Home size={32} /> : <Users size={32} />}
                    </div>
                    <h1 style={{ margin: '0 0 10px', fontSize: '28px' }}>
                        {account.name || 'Conto Condiviso'}
                    </h1>

                    {/* RIepilogo del mio bilancio specifico */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '8px',
                        backgroundColor: 'rgba(255,255,255,0.2)', padding: '12px 24px', borderRadius: '30px', marginTop: '10px'
                    }}>
                        {myBalance > 0.01 ? (
                            <>
                                <CheckCircle size={22} color="#cce8de" />
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Devi ricevere {Math.abs(myBalance).toFixed(2)}€</span>
                            </>
                        ) : myBalance < -0.01 ? (
                            <>
                                <AlertCircle size={22} color="#ffcdd2" />
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Devi dare {Math.abs(myBalance).toFixed(2)}€</span>
                            </>
                        ) : (
                            <>
                                <CheckCircle size={22} color="#ffffff" />
                                <span style={{ fontSize: '18px', fontWeight: 'bold' }}>Sei in pari! (0.00€)</span>
                            </>
                        )}
                    </div>
                </div>

                {/* GRIGLIA SALDI DI TUTTI I MEMBRI */}
                <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)' }}>
                    <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Riepilogo Saldi</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                        {Object.entries(account.memberDetails).map(([uid, details]) => {
                            const bal = balances[uid] || 0;
                            const isMe = uid === currentUser.uid;

                            // Se displayName è In attesa..., usa l'email
                            let displayName = details.displayName;
                            if (details.isPending) {
                                displayName = details.email.split('@')[0];
                            } else {
                                displayName = details.displayName.split(' ')[0];
                            }

                            return (
                                <div key={uid} style={{
                                    padding: '16px',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: '12px',
                                    border: `1px solid ${isMe ? '#006a4e' : 'var(--border-color)'}`,
                                    position: 'relative'
                                }}>
                                    {isMe && <span style={{ position: 'absolute', top: '-10px', right: '-10px', backgroundColor: '#006a4e', color: 'white', fontSize: '11px', padding: '4px 8px', borderRadius: '12px', fontWeight: 'bold' }}>TU</span>}
                                    <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {displayName}
                                    </p>
                                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                                        Ha pagato: {(totalPaidByUser[uid] || 0).toFixed(2)}€
                                    </p>
                                    <p style={{ margin: 0, fontSize: '18px', fontWeight: 'bold', color: getBalanceColor(bal) }}>
                                        {bal > 0.01 ? '+' : ''}{bal.toFixed(2)}€
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* FORM AGGIUNTA SPESA */}
                <div style={{ padding: '20px', backgroundColor: 'var(--bg-secondary)' }}>
                    <button
                        onClick={() => setIsAddOpen(!isAddOpen)}
                        style={{
                            width: '100%', backgroundColor: isAddOpen ? 'var(--bg-tertiary)' : '#006a4e', color: isAddOpen ? 'var(--text-primary)' : '#ffffff',
                            border: 'none', padding: '14px', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '16px', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px'
                        }}
                    >
                        {isAddOpen ? 'Annulla' : <><Plus size={20} /> Aggiungi Nuova Spesa</>}
                    </button>

                    {isAddOpen && (
                        <form onSubmit={handleAddExpense} style={{ marginTop: '20px', backgroundColor: 'var(--bg-primary)', padding: '20px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Descrizione</label>
                                <input
                                    type="text" required value={description} onChange={e => setDescription(e.target.value)}
                                    placeholder="Es. Bolletta Luce, Spesa Conad, Affitto..."
                                    style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '16px' }}
                                />
                            </div>

                            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Importo (€)</label>
                                    <input
                                        type="number" step="0.01" min="0.01" required value={amount} onChange={e => setAmount(e.target.value)}
                                        placeholder="0.00"
                                        style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '16px' }}
                                    />
                                </div>
                                <div style={{ flex: '1 1 200px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Chi ha pagato?</label>
                                    <select
                                        value={paidBy}
                                        onChange={e => setPaidBy(e.target.value)}
                                        style={{ width: '100%', padding: '12px', border: '1px solid var(--border-color)', borderRadius: '6px', fontSize: '16px', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)' }}
                                    >
                                        {Object.entries(account.memberDetails).map(([uid, details]) => (
                                            <option key={uid} value={uid}>
                                                {uid === currentUser.uid ? 'Io (Tu)' : (details.isPending ? details.email : details.displayName)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '8px', border: '1px dashed var(--border-color)' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={excludeMe}
                                        onChange={e => setExcludeMe(e.target.checked)}
                                        style={{ width: '20px', height: '20px' }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>Escludimi dalla divisione</div>
                                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                                            Utile se stai pagando una spesa per conto di altri, o se stai raccogliendo l'affitto per la casa (tu anticipi l'affitto per conto di tutti, padrone di casa compreso).
                                        </div>
                                    </div>
                                </label>
                            </div>

                            <button type="submit" style={{
                                width: '100%', backgroundColor: '#006a4e', color: '#ffffff', border: 'none',
                                padding: '14px', borderRadius: '6px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold'
                            }}>
                                Salva Spesa
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* LISTA SPESE */}
            <h2 style={{ fontSize: '20px', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Receipt size={24} color="var(--text-secondary)" /> Tutte le Spese
            </h2>

            {expenses.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'var(--bg-primary)', borderRadius: '12px', border: '1px dashed var(--border-color)' }}>
                    <p style={{ color: 'var(--text-secondary)' }}>Nessuna spesa inserita in questo gruppo. Aggiungi la prima!</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {expenses.map(exp => {
                        const isMine = exp.paidBy === currentUser.uid;

                        let payerName = "Sconosciuto";
                        if (isMine) {
                            payerName = "Tu";
                        } else if (account.memberDetails[exp.paidBy]) {
                            const details = account.memberDetails[exp.paidBy];
                            payerName = details.isPending ? details.email.split('@')[0] : details.displayName.split(' ')[0];
                        }

                        // Retro-compatibilità e badge divisione
                        let splitText = "Divisa tra tutti";
                        if (exp.splitAmong && exp.splitAmong.length < Object.keys(account.memberDetails).length) {
                            if (!exp.splitAmong.includes(exp.paidBy)) {
                                splitText = "Escluso chi ha pagato";
                            } else {
                                splitText = `Divisa tra ${exp.splitAmong.length}`;
                            }
                        }

                        return (
                            <div key={exp.id} style={{
                                backgroundColor: 'var(--bg-primary)', padding: '16px 20px', borderRadius: '12px',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                borderLeft: `6px solid ${isMine ? '#006a4e' : '#ccc'}`,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                            }}>
                                <div>
                                    <h4 style={{ margin: '0 0 4px', fontSize: '16px', color: 'var(--text-primary)' }}>{exp.description}</h4>
                                    <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        Pagato da <strong>{payerName}</strong>
                                        {' • '}
                                        {exp.timestamp ? new Intl.DateTimeFormat('it-IT', { day: '2-digit', month: 'short' }).format(exp.timestamp.toDate()) : 'Ora'}
                                        {' • '}
                                        <span style={{ fontStyle: 'italic', opacity: 0.8 }}>{splitText}</span>
                                    </p>
                                </div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--text-primary)', textAlign: 'right' }}>
                                    {exp.amount.toFixed(2)}€
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default SharedAccountDetail;
